import { isLoggedInState } from "@/atoms/accountState";
import Dialog from "@/components/Dialog/Dialog";
import { Drawer, DrawerContent } from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  colors,
  isMobile,
  VStack,
  VLine,
  HLine,
  HStack,
  ColorIcon,
  customEvent,
  queryKeys,
} from "goi_common";
import dayjs from "dayjs";
import { useRouter } from "next/router";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useRecoilValue } from "recoil";
import { deleteQuestion } from "../remotes/deleteQuestion";
import { deleteScrap } from "../remotes/deleteScrap";
import { getQuestion } from "../remotes/getQuestion";
import { postScrap } from "../remotes/postScrap";
import { SangjoFuneralDirectorProps } from "@/types/sangjoType";

interface InquiryDrawerProps {
  id?: number;
  isOpen: boolean;
  onClose: () => void;
}

export function InquiryDrawer({ id, isOpen, onClose }: InquiryDrawerProps) {
  const isLoggedIn = useRecoilValue(isLoggedInState);
  const [scrap, setScrap] = useState<boolean>(false);
  const router = useRouter();

  const { data: question, isLoading } = useQuery(queryKeys.question(id), () => getQuestion(id!), {
    enabled: id != null,
  });

  useEffect(() => {
    setScrap(question?.is_scrapped ?? false);
  }, [question?.is_scrapped]);

  const url = typeof window !== "undefined" ? window.location.href : "https://www.goifuneral.co.kr";
  const handleShare = () => {
    const path: "guidebook" | "guidebook_search" = router.asPath.includes("search")
      ? "guidebook_search"
      : "guidebook";
    customEvent({
      newGtm: {
        path,
        trigger_and_target: "qna_modal-share",
      },
    });
    if (isMobile()) {
      navigator.share({
        url,
      });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  const handleScrap = async () => {
    if (!id) return;
    const path: "guidebook" | "guidebook_search" = router.asPath.includes("search")
      ? "guidebook_search"
      : "guidebook";
    customEvent({
      newGtm: {
        path,
        trigger_and_target: "qna_modal-scrap",
      },
    });
    setScrap((s) => !s);
    try {
      if (scrap) await deleteScrap(id);
      else await postScrap(id);
    } catch (e) {
      setScrap((s) => !s);
      alert("스크랩에 실패했습니다. 다시 시도해주세요.");
      console.log(e);
    }
  };

  return (
    <Drawer isOpen={id != null && isOpen} onClose={onClose} placement="bottom">
      <DrawerContent
        width="100% !important"
        height="100%"
        maxWidth="var(--global-max-width) !important"
        marginLeft="var(--left)"
        overflow="auto"
      >
        <CloseHeader>
          <VStack onClick={onClose} style={{ cursor: "pointer" }}>
            <ColorIcon name="icon-x-mono" color={colors.gray900} size={40} />
          </VStack>
        </CloseHeader>
        {!isLoading && question && (
          <>
            <InquiryCard
              type="question"
              status={question.status}
              author={question.author}
              content={question.content}
              date={question.created_at}
              isUsers={question.is_users}
              id={question.id}
              onClose={onClose}
            />
            <HLine color={colors.gray300} width="calc(100% - 48px)" margin="0 auto" />
            {question.answer ? (
              <InquiryCard
                type="answer"
                author={question.answer.author}
                content={question.answer.content}
                date={question.answer.created_at}
              />
            ) : (
              <InquiryCard
                type="waiting"
                content={`잠시만 기다려주세요. 신속하게 답변 드릴 수 있도록 하겠습니다.\n\n운영시간 : 월~금 09:00 ~ 18:00\n문의량이 많을 경우 답변이 지연될 수 있습니다.`}
              />
            )}
          </>
        )}
        {isLoggedIn && question?.answer && (
          <>
            <HLine height={10} color={colors.gray50} />
            <VStack gap={24} padding="40px 24px">
              <div className="subtitle2_b gray900">이 글이 도움이 되셨나요?</div>
              <VStack gap={12}>
                <HStack justifyContent="space-between">
                  <div className="body3_r gray700">가족과 공유해보세요.</div>
                  <ShareButton onClick={handleShare}>
                    <ColorIcon name="share" color="" size={20} />
                    공유
                  </ShareButton>
                </HStack>

                <HStack justifyContent="space-between">
                  <div className="body3_r gray700">스크랩하여 필요할 때 꺼내 보세요.</div>
                  <ShareButton onClick={handleScrap}>
                    <ColorIcon name={scrap ? "heart-fill" : "heart"} color="" size={20} />
                    스크랩
                  </ShareButton>
                </HStack>
              </VStack>
            </VStack>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}

type InquiryCardProps =
  | {
      type: "question";
      status: "답변 대기 중" | "답변 완료";
      author: string;
      content: string;
      date: string;
      isUsers: boolean;
      id: number;
      onClose: () => void;
    }
  | {
      type: "answer";
      status?: never;
      author: SangjoFuneralDirectorProps | null;
      content: string;
      date: string;
      isUsers?: never;
      id?: never;
      onClose?: never;
    }
  | {
      type: "waiting";
      status?: never;
      author?: never;
      content: string;
      date?: never;
      isUsers?: never;
      id?: never;
      onClose?: never;
    };

const labelByType: Record<InquiryCardProps["type"], string> = {
  question: "질문",
  answer: "답변",
  waiting: "답변 대기 중",
};
function InquiryCard({
  type,
  status,
  author,
  content,
  date,
  isUsers,
  id,
  onClose,
}: InquiryCardProps) {
  const router = useRouter();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [blockEditDialogOpen, setBlockEditDialogOpen] = useState<boolean>(false);
  const [dialogLoading, setDialogLoading] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const title = useMemo(() => {
    if (type === "question") {
      return `${author} 님의 질문입니다.`;
    }
    if (type === "answer") {
      if (typeof author === "object") {
        return `${author?.name} 장례지도사의 답변입니다.`;
      } else {
        return "장지윤 고이 장례매니저의 답변입니다.";
      }
    }
  }, [author, type]);

  const handleEdit = useCallback(() => {
    if (status != "답변 대기 중") {
      setBlockEditDialogOpen(true);
      return;
    }

    const query = router.query;
    delete query.id;

    router.push({ pathname: router.pathname, query: { ...query, edit: id } });
  }, [id, router, status]);

  const handleConfirm = useCallback(async () => {
    if (id == null) return;

    try {
      setDialogLoading(true);
      await deleteQuestion(id);
      queryClient.invalidateQueries("questions");
      onClose?.();
    } catch (e) {
      console.log(e);
    } finally {
      setDialogLoading(false);
    }
  }, [id, onClose, queryClient]);

  const handleDelete = useCallback(() => {
    setDialogOpen(true);
  }, []);

  return (
    <>
      <VStack gap={24} padding={`40px 24px ${type === "waiting" ? 50 : 30}px`}>
        <VStack gap={16}>
          <HStack gap={6} alignItems="center">
            <Badge type={type} className="caption1">
              {labelByType[type]}
            </Badge>
            <div className="button2 black">{title}</div>
          </HStack>

          <Content className="body3_r gray900">
            <div dangerouslySetInnerHTML={{ __html: content }} />
          </Content>
        </VStack>

        {type !== "waiting" && (
          <HStack className="body3_m" justifyContent="space-between" alignItems="center">
            <div className="gray500">{dayjs(date).format("YYYY.MM.DD")}</div>
            {isUsers && (
              <HStack alignItems="center">
                <EditButton className="gray600" onClick={handleDelete}>
                  삭제
                </EditButton>
                <VLine color={colors.gray200} height={14} />
                <EditButton className="orange650" onClick={handleEdit}>
                  수정
                </EditButton>
              </HStack>
            )}
          </HStack>
        )}
      </VStack>
      <Dialog
        type={1}
        header="정말 삭제하시겠어요?"
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        isLoading={dialogLoading}
        footerProps={[
          {
            text: "아니오",
            onClick: () => setDialogOpen(false),
          },
          {
            text: "예",
            onClick: handleConfirm,
          },
        ]}
      >
        삭제한 내용은
        <br />
        다시 복구할 수 없습니다.
      </Dialog>

      <Dialog
        type={1}
        isOpen={blockEditDialogOpen}
        onClose={() => setBlockEditDialogOpen(false)}
        header="수정할 수 없습니다."
        footerProps={{
          text: "확인",
          onClick: () => setBlockEditDialogOpen(false),
        }}
      >
        답변이 달린 질문은 수정할 수 없습니다.
      </Dialog>
    </>
  );
}

const CloseHeader = styled(HStack)`
  justify-content: flex-end;
  padding: 11px 24px;
  position: sticky;
  top: 0;
  background: var(--white);
`;
const Badge = styled.div<Pick<InquiryCardProps, "type">>`
  padding: 6px 12px;
  border-radius: 5px;

  ${(props) => {
    switch (props.type) {
      case "question":
        return css`
          background: ${colors.orange100};
          color: ${colors.orange650};
        `;
      case "answer":
        return css`
          background: ${colors.blue150};
          color: ${colors.blue700};
        `;
      case "waiting":
        return css`
          background: ${colors.green50};
          color: ${colors.green450};
        `;
    }
  }}
`;
const Content = styled.div`
  white-space: pre-wrap;
  a {
    color: ${colors.blue700};
    text-decoration: underline;
  }
`;
const EditButton = styled.button`
  padding: 10px 20px;
`;
const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 12px 6px 8px;
  background: var(--gray50);
  color: var(--gray700);
  border-radius: 5px;
`;
